import React, { useMemo, useTransition } from 'react'

import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { CenterText } from '../../../../../../../../../brunswick/components/atoms/CenterText'
import { VisualFilterTile } from '../../../../../../../../../types/graphql/collection-view'
import { useCollectionFilterQueryParamsContext } from '../../../../../../../../containers/CollectionFilterQueryParamsContext'
import { useCollectionFiltersContext } from '../../../containers/CollectionFiltersContext'

import { Body, Figcaption, ImageWrapper, Label, Image as StyledImage } from './styles'

interface Props extends VisualFilterTile {
  visibleTilesDesktop: number
  visibleTilesMobile: number
}

export const VisualFilterSectionTile: React.FC<Props> = ({
  ctaText,
  image,
  id,
  value,
  filter: filterKeyFromField,
  filterSpec,
  visibleTilesDesktop,
  visibleTilesMobile,
}) => {
  const { updateFilterSelection, selectedFilters } = useCollectionFilterQueryParamsContext()
  const { filters } = useCollectionFiltersContext()

  const filterKey = filterSpec?.name ?? filterKeyFromField ?? ''

  const [isUpdateFiltersTransitionPending, startUpdateFiltersTransition] = useTransition()
  const isChecked = useMemo(() => {
    const includedInSelectedFilters = Boolean(selectedFilters[filterKey]?.includes(value))
    return isUpdateFiltersTransitionPending ? !includedInSelectedFilters : includedInSelectedFilters
  }, [filterKey, isUpdateFiltersTransitionPending, selectedFilters, value])

  const filterOptions = useMemo(
    () => filters?.find(f => f.name === filterKey),
    [filters, filterKey]
  )

  const isDisabled = useMemo(
    () => filterOptions?.values.some(o => o.value === value && o.state === 'DISABLED'),
    [filterOptions?.values, value]
  )

  const toggleChecked = () => {
    if (isUpdateFiltersTransitionPending) return
    startUpdateFiltersTransition(() => {
      updateFilterSelection(filterKey, value, !isChecked)
    })
  }

  const letterSpacing = useMemo(() => (isUppercase(ctaText) ? '0.15em' : '0.05em'), [ctaText])

  return (
    <Body
      tileVariantDesktop={'circle'}
      tileVariantMobile={'circle'}
      isDisabled={!!isDisabled}
      isChecked={isChecked}
    >
      <Label htmlFor={`checkbox-${id}`}>
        <input
          disabled={!!isDisabled}
          checked={isChecked}
          type='checkbox'
          id={`checkbox-${id}`}
          style={{ opacity: '0', position: 'absolute', inset: '0' }}
          onChange={toggleChecked}
        />
        <ImageWrapper>
          <StyledImage
            alt={ctaText}
            src={image}
            loading='lazy'
            aspectRatios={{
              sm: 1,
              md: 1,
            }}
            widths={{
              unit: 'vw',
              sm: 100 / visibleTilesMobile,
              md: 100 / visibleTilesDesktop,
            }}
          />
        </ImageWrapper>
      </Label>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {ctaText?.length > 0 ? (
        <Figcaption
          isUppercase={isUppercase(ctaText)}
          isDisabled={isDisabled || false}
          isChecked={isChecked}
          onClick={() => {
            if (!isDisabled) {
              toggleChecked()
            }
          }}
        >
          <CenterText {...{ letterSpacing }}>{ctaText}</CenterText>
        </Figcaption>
      ) : null}
    </Body>
  )
}
